<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import {
        createDropdownButtonKeyHandler,
        focusButtonAtIndex,
    } from './dropdownKeyboardNavigation';
    import countries from '../../countries.json';
    import { getFlagEmoji } from '../../helper';
    import translations from '../../translations';

    import type { Language } from '$lib/types/Language';

    export let open = false;
    export let language: Language = 'en';
    export let handleSelect: (country: string) => void;
    export let filter = '';
    export let inputElement: HTMLInputElement | null = null;

    let dropdownElement: HTMLDivElement;
    let filteredChoices: string[] = [];
    let choices: string[] = Object.keys(countries);
    let focusedIndex = 0;

    const getLocalisedNames = (option: string) => {
        return countries[option as keyof typeof countries];
    };

    const borderRadius = getBorderRadius('dropdownItem');

    $: choices = choices.sort((a, b) =>
        getLocalisedNames(a)[language].localeCompare(getLocalisedNames(b)[language]),
    );

    $: filteredChoices = choices.filter((name) => {
        if (!filter.trim()) {
            return true;
        }

        return Object.values(getLocalisedNames(name)).some((name) =>
            name.toLowerCase().startsWith(filter.toLowerCase()),
        );
    });

    $: if (filteredChoices.length > 0 && focusedIndex >= filteredChoices.length) {
        focusedIndex = 0;
    }

    const handleButtonKeyDown = (event: KeyboardEvent, index: number) => {
        if (!open) return;

        const handler = createDropdownButtonKeyHandler(
            {
                currentIndex: index,
                itemCount: filteredChoices.length,
                returnElement: inputElement,
            },
            (newIndex) => {
                focusedIndex = newIndex;

                if (dropdownElement) {
                    focusButtonAtIndex(dropdownElement, newIndex);
                }
            },
        );

        handler(event);
    };
</script>

<div
    class="fun-my-3x fun-pointer-events-auto fun-max-h-96 fun-w-full fun-scroll-p-2 fun-overflow-y-auto"
>
    {#if !!filteredChoices && filteredChoices.length > 0}
        <div class="fun-grid fun-gap-1 fun-p-2" bind:this={dropdownElement}>
            {#each filteredChoices as option, index (option)}
                {@const optionNames = getLocalisedNames(option)}

                <button
                    id={option}
                    on:click|preventDefault={() => open && handleSelect(optionNames[language])}
                    on:focus={() => (focusedIndex = index)}
                    on:keydown={(e) => handleButtonKeyDown(e, index)}
                    tabindex={open ? 0 : -1}
                    class={cn(
                        'fun-flex fun-w-full fun-min-w-0 fun-items-center fun-gap-2 fun-px-3 fun-py-2 fun-text-sm fun-font-normal fun-outline-none fun-transition-none sm:hover:fun-bg-gray-100 sm:focus:fun-bg-gray-100 md:fun-text-base',
                        borderRadius,
                    )}
                >
                    <span class="fun-flex-shrink-0 fun-text-2xl">{getFlagEmoji(option)}</span>
                    <span class="fun-truncate">
                        <span>{optionNames[language]}</span>
                        {#if optionNames[language] !== optionNames.native}
                            <span>({getLocalisedNames(option).native})</span>
                        {/if}
                    </span>
                </button>
            {/each}
        </div>
    {:else}<p class="fun-p-5 fun-text-sm fun-text-gray-500">
            {translations[language].noCountryFound}
        </p>{/if}
</div>
